import { useState } from 'preact/hooks';
import { GitHubVaultFS } from '@hud/adapters/github-vault';
import type { Connection, GitHubConnection } from './connection.js';
import './setup.css';

export interface SetupProps {
  onConnect: (connection: Connection) => void;
  /** Prefilled when re-opening settings on an existing connection. */
  initial?: Connection | null;
  onCancel?: () => void;
}

type TestState =
  | { status: 'idle' }
  | { status: 'testing' }
  | { status: 'ok'; files: number }
  | { status: 'failed'; message: string };

/**
 * First-run connection screen.
 *
 * Deliberately verifies before saving: a token typo that only surfaces as an
 * empty dashboard is miserable to diagnose, so we make the round trip here and
 * report what actually came back.
 */
export function Setup({ onConnect, initial, onCancel }: SetupProps) {
  const start = initial?.kind === 'github' ? initial : null;
  const [owner, setOwner] = useState(start?.owner ?? '');
  const [repo, setRepo] = useState(start?.repo ?? '');
  const [branch, setBranch] = useState(start?.branch ?? 'main');
  const [token, setToken] = useState(start?.token ?? '');
  const [test, setTest] = useState<TestState>({ status: 'idle' });

  const ready = owner.trim() && repo.trim() && token.trim();

  const connection = (): GitHubConnection => ({
    kind: 'github',
    owner: owner.trim(),
    repo: repo.trim(),
    branch: branch.trim() || 'main',
    token: token.trim(),
  });

  const submit = async (event: Event) => {
    event.preventDefault();
    if (!ready) return;

    setTest({ status: 'testing' });
    const target = connection();

    try {
      const fs = new GitHubVaultFS(target);
      const entries = await fs.list();
      setTest({ status: 'ok', files: entries.length });
      onConnect(target);
    } catch (err) {
      setTest({ status: 'failed', message: err instanceof Error ? err.message : String(err) });
    }
  };

  return (
    <div class="hud-setup">
      <form class="hud-setup__card" onSubmit={submit}>
        <h1 class="hud-setup__title">Connect your vault</h1>
        <p class="hud-setup__lede">
          The HUD reads and writes markdown in a private GitHub repo. Point it at yours.
        </p>

        <label class="hud-setup__field">
          <span>Owner</span>
          <input
            value={owner}
            placeholder="your-github-username"
            autocapitalize="off"
            autocorrect="off"
            spellcheck={false}
            onInput={(e) => setOwner((e.target as HTMLInputElement).value)}
          />
        </label>

        <label class="hud-setup__field">
          <span>Repository</span>
          <input
            value={repo}
            placeholder="hud-vault"
            autocapitalize="off"
            autocorrect="off"
            spellcheck={false}
            onInput={(e) => setRepo((e.target as HTMLInputElement).value)}
          />
        </label>

        <label class="hud-setup__field">
          <span>Branch</span>
          <input
            value={branch}
            placeholder="main"
            autocapitalize="off"
            autocorrect="off"
            spellcheck={false}
            onInput={(e) => setBranch((e.target as HTMLInputElement).value)}
          />
        </label>

        <label class="hud-setup__field">
          <span>Access token</span>
          <input
            type="password"
            value={token}
            placeholder="github_pat_…"
            autocapitalize="off"
            autocorrect="off"
            spellcheck={false}
            onInput={(e) => setToken((e.target as HTMLInputElement).value)}
          />
        </label>

        <details class="hud-setup__help">
          <summary>How to make a token</summary>
          <ol>
            <li>
              GitHub → Settings → Developer settings → Personal access tokens →{' '}
              <strong>Fine-grained tokens</strong>
            </li>
            <li>
              Repository access: <strong>Only select repositories</strong> → your vault repo
            </li>
            <li>
              Permissions → Repository → <strong>Contents: Read and write</strong>. Nothing else.
            </li>
          </ol>
          <p>
            Scope it to the one repo. The token is stored in this browser, so its reach should
            stop at your vault.
          </p>
        </details>

        {test.status === 'failed' ? <p class="hud-setup__error">{test.message}</p> : null}
        {test.status === 'ok' ? (
          <p class="hud-setup__ok">Connected — {test.files} files found.</p>
        ) : null}

        <div class="hud-setup__actions">
          {onCancel ? (
            <button type="button" class="hud-setup__secondary" onClick={onCancel}>
              Cancel
            </button>
          ) : null}
          <button type="submit" disabled={!ready || test.status === 'testing'}>
            {test.status === 'testing' ? 'Checking…' : 'Connect'}
          </button>
        </div>
      </form>
    </div>
  );
}
